import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCreditsData } from "@/hooks/useCreditsData";
import { useCheckout, useAutoRefillSave } from "@/hooks/useCreditsActions";
import { usePaymentReturn } from "@/hooks/usePaymentReturn";
import { Layout } from "@/components/Layout";
import { PaymentResultBanner } from "@/components/credits/PaymentResultBanner";
import { BalanceCard } from "@/components/credits/BalanceCard";
import { MonthlySpendCard } from "@/components/credits/MonthlySpendCard";
import { PurchaseCard } from "@/components/credits/PurchaseCard";
import { AutoRefillCard } from "@/components/credits/AutoRefillCard";
import { PaidLineInfo } from "@/components/credits/PaidLineInfo";
import { TransactionsList } from "@/components/credits/TransactionsList";
import { ArrowLeft, Lock } from "lucide-react";

export default function BuyCredits() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const credits = useCreditsData();
  const { balance, transactions, loadBalance, loadTransactions } = credits;
  const [amount, setAmount] = useState("25");

  const onPaid = useCallback(() => {
    loadBalance();
    loadTransactions();
  }, [loadBalance, loadTransactions]);
  const { payState, paidAmount } = usePaymentReturn(onPaid);

  const amountNum = parseFloat(amount);
  const amountValid = !Number.isNaN(amountNum) && amountNum >= 5 && amountNum <= 500;
  const { starting, startCheckout } = useCheckout(amountNum, amountValid);
  const { arSaving, saveAutoRefill } = useAutoRefillSave(credits, onPaid);

  return (
    <Layout>
      <div className="max-w-5xl w-full mx-auto px-6 py-8 space-y-6" data-testid="buy-credits-page">
        {!user?.locked && (
          <button onClick={() => navigate("/")} data-testid="credits-back-btn"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
        )}

        {user?.locked && (
          <div data-testid="credits-locked-banner"
            className="flex items-start gap-3 border border-amber-500/40 bg-amber-500/10 px-4 py-3 rounded-sm">
            <Lock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-100">Your credits have run out</p>
              <p className="text-xs text-amber-200/80 mt-1">
                The rest of the app is locked until you add credits. Purchase any amount below to unlock your dashboard,
                inquiries, and the call-in line again.
              </p>
            </div>
          </div>
        )}

        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Call Credits</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            Credits pay for calls to our voice avatar line. Your incident records always stay on this site
            unless you delete them — credits only cover the phone calls.
          </p>
        </div>

        <PaymentResultBanner state={payState} paidAmount={paidAmount} />

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <BalanceCard balance={balance} />
            <MonthlySpendCard balance={balance} />
            <PurchaseCard amount={amount} setAmount={setAmount} amountNum={amountNum}
              amountValid={amountValid} starting={starting} onCheckout={startCheckout} />
            <AutoRefillCard enabled={credits.arEnabled} setEnabled={credits.setArEnabled}
              threshold={credits.arThreshold} setThreshold={credits.setArThreshold}
              refillAmount={credits.arAmount} setRefillAmount={credits.setArAmount}
              hasSavedCard={balance ? balance.has_saved_card : null}
              saving={arSaving} onSave={saveAutoRefill} />
          </div>
          <div className="space-y-6">
            <PaidLineInfo />
            <TransactionsList transactions={transactions} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
